import React, { useCallback, useEffect, useState } from "react";
import { TenantService } from "services/tenant";
// import { useNavigate } from "react-router";
import Loader from "components/Loader/Loader";
import { FaTrash } from "react-icons/fa";
import Modal from "components/common/Modal/Modal";
import AddEditTenantForm from "components/features/Add-Edit-Tenant-Form/Add-Edit-Tenant-Form";
import { formatCurrency } from "ultils/currency-formatter";
import { getOrdinalSuffix } from "ultils/ordinal";
import { formatDate } from "ultils/date-formatter";
import Banner from "components/Banner/banner";

const TenantManagement: React.FC = () => {
  //   const navigate = useNavigate();
  const [tenantList, setTenantList] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [title, setTitle] = useState("Edit Tenant");
  const [isEditMode, setIsEditMode] = useState(false);

  const [selectedTile, setSelectedTile] = useState<{} | any>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [inputValue, setInputValue] = useState<string>(""); // State to track the input value
  const [isDeleteEnabled, setIsDeleteEnabled] = useState<boolean>(false); // State to enable/disable delete button

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  const openDeleteModal = () => setIsDeleteModalOpen(true);
  const closeDeleteModal = () => setIsDeleteModalOpen(false);

  const fetchTenants = useCallback(async () => {
    try {
      const data = await TenantService.fetchAllTenants();
      console.log(data);
      setTenantList(data.tenants);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const deleteTenantClicked = (selectedTile: any) => {
    setSelectedTile(selectedTile);
    setTitle("Delete Tenant: " + selectedTile.fullName);
    openDeleteModal();
  };

  const deleteTenant = async () => {
    try {
      // debugger;
      await TenantService.deleteTenant(selectedTile.tenant.id);
      // Reload properties after successful deletion
      await fetchTenants();
      closeDeleteModal();
      alert("Tenant deleted successfully");
    } catch (error) {
      console.error("Error deleting tenant:", error);
      alert("Failed to delete tenant. Please try again.");
    }
  };

  const editTenantClicked = (selectedTile: any) => {
    setSelectedTile(selectedTile.tenant);
    setIsEditMode(true);
    setTitle("Edit Tenant: " + selectedTile.fullName);
    openModal();
  };

  const determineAddOrUpdate = async (
    tenantDetailsForm: any,
    e: React.FormEvent
  ) => {
    e.preventDefault();

    try {
      if (!isEditMode) {
        await TenantService.postTenant(tenantDetailsForm);
        alert("Tenant details added successfully");
      } else {
        await TenantService.updateTenant(tenantDetailsForm);
        alert("Tenant details updated successfully");
      }
      closeEditModalBtn();
      fetchTenants();
    } catch (err) {
      console.error("Failed to add/update tenant:", err);
      alert(
        "Failed to add/update tenant details, please refresh and try again."
      );
    }
  };

  const closeEditModalBtn = () => {
    setSelectedTile({});
    closeModal();
  };

  const closeDeleteModalBtn = () => {
    closeDeleteModal();
    setInputValue("");
  };

  // Handle the input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    // Enable the delete button if the input matches the selected street address
    if (e.target.value === selectedTile.fullName) {
      setIsDeleteEnabled(true);
    } else {
      setIsDeleteEnabled(false);
    }
  };

  return (
    <div className="bg">
      <h1 className="dashboard-header">Manage your Tenants</h1>
      {error ? <div>Error: {error}</div> : ""}
      {!loading && tenantList.length === 0 ? (
        <div>
          <p>Add a Property and a Tenant to get started.</p>
        </div>
      ) : null}

      {loading ? (
        <Loader />
      ) : (
        <div
          className="tiles-container"
          style={{
            gap: "8.5rem",
            gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
          }}
        >
          {tenantList.map((tenant, index) => (
            <div key={index} className="tile">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {tenant.tenant.active ? (
                  <span className="status-badge paid">Active</span>
                ) : (
                  <span className="status-badge not-paid">Not Active</span>
                )}{" "}
                <div
                  style={{ cursor: "pointer" }}
                  onClick={() => deleteTenantClicked(tenant)}
                >
                  <FaTrash style={{ color: "red" }} />
                </div>
              </div>
              <h2>
                {tenant.fullName}
                {" - "}
                {tenant.unit ? tenant.unit.name : tenant.property.streetAddress}
              </h2>
              <p>{tenant.tenant.phoneNumber}</p>
              <p>
                current monthly rent:{" "}
                <b>
                  {formatCurrency(
                    tenant.unit
                      ? tenant.unit.actualRent
                      : tenant.property.actualRent
                  )}
                </b>{" "}
                due on every <b>{getOrdinalSuffix(tenant.tenant.rentDueDay)}</b>{" "}
                of the month
              </p>
              <p>
                Lease started on{" "}
                <b>
                  {formatDate(tenant.tenant.leaseStartDate, "MMMM dd, yyyy")}
                </b>{" "}
                and Ends on{" "}
                <b>{formatDate(tenant.tenant.leaseEndDate, "MMMM dd, yyyy")}</b>
              </p>
              <p>{tenant.description}</p>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "12px",
                  marginTop: "20px",
                }}
              >
                <button
                  className="btn"
                  onClick={() => editTenantClicked(tenant)}
                >
                  Edit
                </button>
                <button className="btn">Moved Out?</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal title={title} isOpen={isModalOpen} onClose={closeEditModalBtn}>
        <AddEditTenantForm
          propertyDetails={{}}
          onTenantUpdate={determineAddOrUpdate}
          unitDetails={{}}
          tenantDetails={selectedTile}
        />
      </Modal>

      <Modal
        title={"Delete Tenant?"}
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModalBtn}
      >
        <div>
          <Banner
            type="warning"
            header={"Are you sure you want to delete this tenant?"}
            body={
              "Deleting this tenant will remove all payment history and financial data from your portfolio, and you will no longer see it on your dashboard. Financial data will NOT be kept."
            }
          />

          <p>Please enter the Tenant's name:</p>
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            placeholder="Enter Tenant Name"
          />
          <p>
            <b>{selectedTile.fullName}</b> - this is case sensitive. Please
            write it exactly as it is displayed.
          </p>
          <div className="header-container">
            <button
              className="btn"
              onClick={closeDeleteModalBtn}
              style={{ backgroundColor: "gray", color: "white" }}
            >
              Cancel
            </button>
            <button
              className="btn"
              onClick={deleteTenant}
              disabled={!isDeleteEnabled}
              style={{ backgroundColor: "red", color: "white" }}
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TenantManagement;
